<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { emitter } from 'shared/helpers/mitt';
import KanbanAPI from '../../../../api/kanban';

// Props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  isStacklab: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits([
  'update:item',
  'item-updated',
]);

const { t } = useI18n();
const store = useStore();

// Computed para obter os funis
const funnels = computed(() => store.getters['funnel/getFunnels'] || []);

// Novo computed para merge de globais + customizados do item
const allCustomAttributes = computed(() => {
  // Busca o funil do item
  const funnel = funnels.value.find(
    f => String(f.id) === String(props.item?.funnel_id)
  );
  const globalAttrs = Array.isArray(funnel?.global_custom_attributes)
    ? funnel.global_custom_attributes
    : [];
  const itemAttrs = Array.isArray(props.item?.item_details?.custom_attributes)
    ? props.item?.item_details.custom_attributes
    : [];
  // Merge: para cada global, pega valor do item se existir
  const merged = globalAttrs.map(globalAttr => {
    const itemAttr = itemAttrs.find(
      a => a.name === globalAttr.name && a.type === globalAttr.type
    );
    return {
      name: globalAttr.name,
      type: globalAttr.type,
      value: itemAttr ? itemAttr.value : '',
      _isGlobal: true,
    };
  });
  // Adiciona os customizados do item que não são globais
  const onlyItemAttrs = itemAttrs.filter(
    attr => !globalAttrs.some(g => g.name === attr.name && g.type === attr.type)
  );
  return [...merged, ...onlyItemAttrs];
});

// Ref editável para os campos
const allCustomAttributesEditable = ref([]);

// Sincroniza ref editável ao trocar de item
watch(
  () => allCustomAttributes.value,
  newAttrs => {
    if (newAttrs && Array.isArray(newAttrs)) {
      allCustomAttributesEditable.value = newAttrs.map(attr => ({ ...attr }));
    } else {
      allCustomAttributesEditable.value = [];
    }
  },
  { immediate: true, deep: true }
);

// Função para atualizar o valor de um atributo
function updateAllCustomAttributeValue(idx, newValue) {
  allCustomAttributesEditable.value[idx].value = newValue;
}

// Função para salvar alterações
async function saveAllCustomAttributes() {
  try {
    // Salva todos como custom_attributes do item
    const attrs = allCustomAttributesEditable.value.map(attr => ({
      name: attr.name,
      type: attr.type,
      value: attr.value,
    }));
    const payload = {
      ...props.item,
      custom_attributes: attrs,
      item_details: {
        ...props.item?.item_details,
        custom_attributes: attrs,
      },
    };
    const { data } = await KanbanAPI.updateItem(props.item?.id, payload);
    emit('update:item', data);
    emit('item-updated');

    // Mostrar mensagem de sucesso
    emitter.emit('newToastMessage', {
      message: 'Campos personalizados salvos com sucesso',
      action: { type: 'success' },
    });

  } catch (error) {
    console.error('Erro ao salvar campos personalizados:', error);
    emitter.emit('newToastMessage', {
      message: 'Erro ao salvar campos personalizados',
      action: { type: 'error' },
    });
  }
}
</script>

<template>
  <div class="space-y-4">
    <template v-if="allCustomAttributesEditable.length === 0">
      <p class="text-sm text-slate-500">
        Nenhum campo personalizado disponível.
      </p>
    </template>
    <div
      v-for="(attr, idx) in allCustomAttributesEditable"
      :key="attr.name + '-' + attr.type"
      class="flex flex-col gap-2"
    >
      <label class="font-medium flex items-center gap-1 text-sm text-slate-700 dark:text-slate-300">
        <fluent-icon
          v-if="attr._isGlobal"
          icon="globe"
          size="14"
          class="text-woot-500 align-middle flex-shrink-0"
          title="Campo global do funil"
        />
        <span>{{ attr.name }}</span>
      </label>
      <input
        v-if="attr.type === 'string'"
        v-model="attr.value"
        type="text"
        class="w-full input"
        @change="updateAllCustomAttributeValue(idx, attr.value)"
      />
      <input
        v-else-if="attr.type === 'number'"
        v-model.number="attr.value"
        type="number"
        class="w-full input"
        @change="updateAllCustomAttributeValue(idx, attr.value)"
      />
      <input
        v-else-if="attr.type === 'date'"
        v-model="attr.value"
        type="date"
        class="w-full input"
        @change="updateAllCustomAttributeValue(idx, attr.value)"
      />
      <input
        v-else
        v-model="attr.value"
        type="text"
        class="w-full input"
        @change="updateAllCustomAttributeValue(idx, attr.value)"
      />
    </div>
    <button
      class="primary-button mt-4"
      @click="saveAllCustomAttributes"
    >
      Salvar
    </button>
  </div>
</template>

<style scoped>
.input {
  @apply px-3 py-2 text-sm border border-slate-200 bg-slate-50 rounded-lg focus:ring-1 focus:ring-woot-500 focus:border-woot-500 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300;
}

.primary-button {
  @apply px-4 py-2 text-sm font-medium text-white
         bg-woot-500 hover:bg-woot-600
         dark:bg-woot-600 dark:hover:bg-woot-700
         rounded-lg transition-colors;

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}
</style>
